// FAQSection.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'Do you guarantee admission?',
      answer: 'No. Admission decisions are made solely by academic institutions based on their own criteria and standards. Our role is to prepare strong, competitive applications and guide you through the process based on your eligibility and profile. We provide honest assessments of your chances and work to present your application in the best possible light, but final admission decisions rest entirely with the universities. This ensures transparency and maintains the integrity of the academic admission process.'
    },
    {
      question: 'Is the initial consultation free?',
      answer: 'Yes. The first consultation is completely free and comes with no obligation. This session is designed to assess your academic background, discuss your goals and preferred destinations, evaluate your eligibility for various programs, and provide you with a clear understanding of the process, timelines, and realistic options available to you. It is an opportunity for you to ask questions and for us to determine how we can best support your study abroad journey.'
    },
    {
      question: 'Do you have partnerships with universities?',
      answer: 'Yes. We have established partnerships with over 100 academic institutions across the USA, Canada, the UK, Australia, and Europe. These relationships have been built over years of consistent collaboration and successful placements. Our partnerships allow us to support applications more effectively, provide insights into institutional requirements, and in some cases, identify scholarship or tuition discount opportunities. However, these partnerships do not override institutional admission standards or guarantee acceptance. All candidates must meet the academic and eligibility requirements set by each institution.'
    },
    {
      question: 'Can you help me get a scholarship?',
      answer: 'We actively explore available scholarship and tuition discount opportunities through our partner institutions for eligible candidates. Where applicable, candidates may receive tuition reductions typically ranging from 10 percent to 40 percent, depending on their academic profile, chosen program, and availability at the time of application. These opportunities are competitive and not guaranteed. We assess your eligibility during the consultation process and work to identify the best possible financial support options for your situation. All scholarship recommendations are made transparently and based on institutional criteria.'
    },
    {
      question: 'Who prepares my Statement of Purpose?',
      answer: 'Our experienced team prepares and refines your Statement of Purpose in close collaboration with you. We work together to ensure the statement accurately reflects your academic goals, background, motivations, and future aspirations while aligning with the specific expectations of your target institutions. You provide the personal insights and experiences, and we help structure and articulate them in a compelling, professional manner that strengthens your application. The final statement is a true representation of you, enhanced by our expertise in academic writing and institutional requirements.'
    },
    {
      question: 'Who do I pay for tuition?',
      answer: 'All tuition fees and school related payments are made directly to the academic institution, not to Notapaths. We do not collect, hold, or process tuition payments on behalf of universities. This ensures complete transparency and security in your financial transactions. Once you receive your admission offer and decide to accept, you will make payment arrangements directly with the institution according to their specified payment schedules and methods. We can provide guidance on understanding fee structures and payment processes, but all financial transactions remain between you and the university.'
    },
    {
      question: 'Will I communicate directly with the university?',
      answer: 'Yes. Once initial contact is established and your application is submitted, all formal communication including admission decisions, scholarship offers, enrollment instructions, and official correspondence will be directly between you and the institution. You will receive all official documents and letters in your name from the university. While this direct communication is taking place, we remain available to guide you, help you understand any correspondence, advise on next steps, and support you throughout the enrollment and preparation process. We act as your advisor and support system, not as an intermediary for official institutional communication.'
    },
    {
      question: 'Can I apply to more than one school?',
      answer: 'Yes, absolutely. In fact, applying to multiple institutions is often a strategic approach to increase your chances of admission and give you options to compare. Depending on your academic profile, preferred destinations, and program interests, we may recommend and support applications to several institutions simultaneously. This allows you to have backup options and the ability to choose the best fit from multiple offers. We help you identify the right mix of institutions based on your goals and eligibility, and manage the application process for each one efficiently.'
    },
    {
      question: 'What if I am unsure about my eligibility?',
      answer: 'That is exactly what the free initial consultation is designed to address. During this session, we thoroughly review your academic background, qualifications, work experience if applicable, English language proficiency, and any other relevant factors. We then provide you with honest, realistic feedback about your eligibility for various programs and destinations. If there are gaps or areas that need strengthening, we will advise you on how to address them. Our goal is to give you a clear, truthful assessment so you can make informed decisions about your study abroad plans without any surprises later in the process.'
    },
    {
      question: 'Do you support undergraduate and postgraduate programs?',
      answer: 'Yes. We provide comprehensive support for undergraduate degree programs, postgraduate programs including Masters and PhD pathways, and selected professional programs across various fields of study. Our services are tailored to the specific requirements of each program level, as admission criteria, documentation needs, and application processes can vary significantly. During your consultation, we will discuss which program level aligns with your qualifications and goals, and ensure you understand the specific requirements and timelines for your chosen path. All support is subject to eligibility and institutional requirements.'
    },
    {
      question: 'What if I decide not to proceed after the consultation?',
      answer: 'There is absolutely no obligation to proceed after the free consultation. The consultation is an opportunity for you to gather information, understand your options, and assess whether our services align with your needs and goals. If you decide not to move forward for any reason, that is completely acceptable and there will be no pressure or follow up beyond a courtesy check in. You are free to take the guidance and information provided during the consultation and make whatever decision is best for you. Our goal is to provide value and clarity, whether or not you choose to engage our services further.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            Clear answers to common questions about our Study Abroad support services
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Question Button */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-[#F5F8FA] transition-colors"
              >
                <span className="text-base font-semibold text-[#202356] pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={20} className="text-[#202356]" />
                  ) : (
                    <Plus size={20} className="text-[#202356]" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0 bg-white">
                      <p className="text-gray-600 text-sm font-light leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
